import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { DynamicForm } from '../form/DynamicForm';
import Toast from '../ui/toast';
import { UserOutlined, IdcardOutlined, MailOutlined, PhoneOutlined, LockOutlined } from '@ant-design/icons';
import Api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * UserFormModal
 * Props:
 *   open: boolean
 *   user: objeto do usuário (null para criação)
 *   onCancel: função de fechamento
 *   onSave: async(form) => {}
 */
const UserFormModal = ({ open, user = null, onCancel, onSave }) => {
  const { getUserInfo } = useAuth();
  const { theme } = useTheme();
  const isEdit = Boolean(user);
  const [form, setForm] = useState({
    username: '',
    fullName: '',
    email: '',
    whatsapp: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit && user) {
      setForm({
        username: user.username || user.userName || '',
        fullName: user.name || user.fullName || '',
        email: user.email || '',
        whatsapp: user.whatsapp || user.phoneNumber || '',
        password: '',
        confirmPassword: '',
      });
    } else {
      setForm({ 
        username: '', 
        fullName: '', 
        email: '', 
        whatsapp: '', 
        password: '', 
        confirmPassword: '' 
      });
    }
  }, [user, isEdit, open]);


  // Simplificada pois as validações estão no DynamicForm agora
  const validate = () => {
    // Validações básicas já são feitas pelo DynamicForm
    // Esta função pode ser usada para validações customizadas adicionais se necessário
    return true;
  };

  const handleOk = async (formData) => {
    setLoading(true);
    try {
      // O DynamicForm já passou os dados validados
      if (onSave) {
        const response = await onSave(formData, isEdit);

        console.log(response);
        if(response === true){
          onCancel();
        }
      }
 
    } catch (e) {
      console.error('Erro ao salvar usuário:', e);
      Toast.error(e.message || 'Erro ao salvar usuário.');
      // Não fecha o modal em caso de erro
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Editar Usuário' : 'Criar Usuário'}
      open={open}
      onCancel={onCancel}
      footer={null}
      style={{ borderRadius: '12px' }}
      width={520}
    >
      <DynamicForm
        formConfig={[{
          columns: 1,
          questions: [
            { 
              id: 'username', 
              label: 'Usuário', 
              type: 'text', 
              required: true, 
              prefixIcon: <UserOutlined />,
              customValidation: {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value.length < 3) {
                    return Promise.reject(new Error('O usuário deve ter pelo menos 3 caracteres!'));
                  }
                  return Promise.resolve();
                },
              }
            },
            { 
              id: 'fullName', 
              label: 'Nome completo', 
              type: 'text', 
              required: true,
              prefixIcon: <IdcardOutlined />,
              customValidation: {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value.length < 2) {
                    return Promise.reject(new Error('O nome deve ter pelo menos 2 caracteres!'));
                  }
                  return Promise.resolve();
                },
              }
            },
            { 
              id: 'email', 
              label: 'E-mail', 
              type: 'email', 
              required: true, 
              prefixIcon: <MailOutlined /> 
            },
            { 
              id: 'whatsapp', 
              label: 'WhatsApp', 
              type: 'phone', 
              prefixIcon: <PhoneOutlined /> 
            },
            // Só mostrar campos de senha para criação
            ...(isEdit ? [] : [
              { 
                id: 'password', 
                label: 'Senha', 
                type: 'password', 
                required: true, 
                prefixIcon: <LockOutlined />,
                minLength: 8,
                requireUppercase: true,
                requireLowercase: true,
                requireNumbers: true,
                requireSpecialChars: true
              },
              { 
                id: 'confirmPassword', 
                label: 'Confirmar senha', 
                type: 'confirmPassword', 
                required: true, 
                prefixIcon: <LockOutlined />,
                compareField: 'password'
              },
            ])
          ]
        }]}
        values={form}
        setValues={(v) => setForm(v)}
        submitText={isEdit ? 'Salvar' : 'Criar'}
        onSubmit={handleOk}
        inputPadding="6px 10px"
        loading={loading}
        disabled={loading}
        submitButtonStyle={{ backgroundColor: theme.primary, color: '#fff', border: 'none' }}
        onClose={onCancel}
      />
    </Modal>
  );
};

export default UserFormModal; 